import { Intent, OverlayToaster, Position, Toast } from '@blueprintjs/core';
import cn from 'classnames';
import ApplicationError, { ErrorSeverity } from 'es/app/business-logic/ApplicationError';
import { INotification } from 'es/app/business-logic/Notification';
import { useAppStore } from 'es/app/store/GlobalStore';
import { BootstrapStyles } from 'es/app/visuals/styles/BootstrapStyles';
import withSuspense from 'es/core/hocs/withSuspense';
import { useI18n } from 'es/core/hooks/useI18n';
import Guid from 'es/core/types/Guid';
import { observer } from 'mobx-react';
import React, { MouseEvent, useState } from 'react';

const NotificationDetails = (props: { htmlContent: string; className?: string }) => {
    const [showDetails, setShowDetails] = useState(false);
    const handleToggleDetails = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setShowDetails(prev => !prev);
    };

    if (!props.htmlContent) return null;

    const className = cn('notification-toast-details', props.className);
    const html = { __html: showDetails ? props.htmlContent : '' };

    return (
        <>
            <div className={className} dangerouslySetInnerHTML={html} />
            <button className="link-button" onClick={handleToggleDetails}>
                {showDetails ? 'Hide' : 'Show'} details
            </button>
        </>
    );
};

const NotificationToast = withSuspense((props: { notification: INotification; onDismissed: (id: Guid) => void }) => {
    const { notification, onDismissed } = props;
    const handleDismissed = () => onDismissed(notification.id);

    const html = { __html: notification.content ?? '' };

    return (
        // There is a bug in the blueprintjs Toast2 that causes the toast to be dismissed when
        // it is focused if the timeout is set to 0. So we use the deprecated Toast component
        // for now. See: https://github.com/palantir/blueprint/issues/6742
        <Toast
            onDismiss={handleDismissed}
            intent={notification.intent ?? Intent.NONE}
            message={
                <div className="application-toast">
                    <div className="application-toast-header">
                        <strong className={BootstrapStyles['me-auto']}>{notification.title}</strong>
                    </div>

                    <div className="application-toast-body">
                        <span dangerouslySetInnerHTML={html} />
                        <NotificationDetails htmlContent={notification.details ?? ''} />
                    </div>
                </div>
            }
        />
    );
});

const ErrorDetails = (props: { error: ApplicationError }) => {
    const {
        error: { details },
    } = props;
    if (!details) return null;

    const html = details.replace(/\n/g, '<br />').replace(/\s{2}/g, '&nbsp;&nbsp;');

    return <NotificationDetails htmlContent={html} className="error-toast-details" />;
};

const ErrorToast = withSuspense(
    (props: { error: ApplicationError; onDismissed: (error: ApplicationError) => void }) => {
        const { l10n } = useI18n('app', 'error');

        const { error, onDismissed } = props;

        const handleErrorDismissed = () => {
            onDismissed(error);
        };

        const source = error.source ?? l10n('applicationError.title');
        const headerText = error.count === 1 ? source : `${source} (${error.count})`;
        const intent = error.severity === ErrorSeverity.Warning ? Intent.WARNING : Intent.DANGER;

        return (
            <Toast
                timeout={0}
                onDismiss={handleErrorDismissed}
                intent={intent}
                message={
                    <div className="application-toast">
                        <div className="application-toast-header">
                            <strong
                                className={BootstrapStyles['me-auto']}
                                dangerouslySetInnerHTML={{ __html: headerText }}
                            />
                        </div>

                        <div className="application-toast-body">
                            <span dangerouslySetInnerHTML={{ __html: error.message ?? '' }} />
                            &nbsp;
                            <ErrorDetails {...props} />
                        </div>
                    </div>
                }
            />
        );
    }
);

const ApplicationToaster = () => {
    const store = useAppStore();

    return (
        <OverlayToaster position={Position.BOTTOM_RIGHT} canEscapeKeyClear={false} className="application-toasts">
            {store.errors.map(err => (
                <ErrorToast key={err.id} error={err} onDismissed={store.dismissError} />
            ))}

            {store.notifications.map(notification => (
                <NotificationToast
                    key={notification.id}
                    notification={notification}
                    onDismissed={store.dismissNotification}
                />
            ))}
        </OverlayToaster>
    );
};

export default observer(ApplicationToaster);
